import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Card, EmptyState, Pill, Skeleton } from '@/ui/primitives'
import { lei } from '@/lib/money'

type Rand = {
  partner: string; total_invoiced: number; total_paid: number
  total_paid_ron: number; total_paid_eur: number; advance: number
  reservation_count: number; invoice_count: number; phone: string | null
  mobile: string | null; email: string | null; status: string | null
}
type Lista = {
  advances: Rand[]
  summary: {
    total_clients: number; clients_with_advance: number; clients_with_debt: number
    total_advances: number; total_debts: number; net_position: number
  }
}

export function Advances() {
  const [filtru, setFiltru] = useState<'toti' | 'avans' | 'datorie'>('toti')
  const { data, isPending } = useQuery({ queryKey: ['avansuri'], queryFn: () => api.get<Lista>('/advances/') })

  const rows = (data?.advances ?? []).filter((r) =>
    filtru === 'avans' ? r.advance > 0 : filtru === 'datorie' ? r.advance < 0 : true)
  const s = data?.summary

  return (
    <>
      <PageHeader title="Avansuri si solduri" count={s ? `${s.total_clients} clienti` : undefined} />
      <div className="px-8 py-6 space-y-5">
        {s && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className="p-5">
              <p className="text-[12.5px] text-muted">Avansuri incasate</p>
              <p className="mt-1.5 tnum text-[24px] text-good leading-none">{lei(s.total_advances)} lei</p>
              <p className="mt-1.5 text-[12.5px] text-muted">{s.clients_with_advance} clienti au platit in avans</p>
            </Card>
            <Card className="p-5">
              <p className="text-[12.5px] text-muted">Datorii</p>
              <p className="mt-1.5 tnum text-[24px] text-warn leading-none">{lei(s.total_debts)} lei</p>
              <p className="mt-1.5 text-[12.5px] text-muted">{s.clients_with_debt} clienti au de plata</p>
            </Card>
            <Card className="p-5">
              <p className="text-[12.5px] text-muted">Pozitie neta</p>
              <p className={'mt-1.5 tnum text-[24px] leading-none ' + (s.net_position < 0 ? 'text-warn' : 'text-good')}>
                {lei(s.net_position)} lei
              </p>
              <p className="mt-1.5 text-[12.5px] text-muted">
                {s.net_position < 0 ? 'clientii ne datoreaza mai mult decat au avansat' : 'avem incasat in avans'}
              </p>
            </Card>
          </div>
        )}

        <div className="flex gap-2">
          <Pill active={filtru === 'toti'} onClick={() => setFiltru('toti')}>Toti</Pill>
          <Pill active={filtru === 'avans'} onClick={() => setFiltru('avans')}>Cu avans</Pill>
          <Pill active={filtru === 'datorie'} onClick={() => setFiltru('datorie')}>Cu datorie</Pill>
        </div>

        <Card className="overflow-hidden">
          {isPending ? <Skeleton /> : rows.length === 0 ? <EmptyState title="Niciun client aici" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="text-left text-[11.5px] uppercase tracking-[0.1em] text-muted">
                    <th className="font-medium px-5 py-3">Client</th>
                    <th className="font-medium px-5 py-3">Contact</th>
                    <th className="font-medium px-5 py-3 text-right">Facturat</th>
                    <th className="font-medium px-5 py-3 text-right">Platit</th>
                    <th className="font-medium px-5 py-3 text-right">Sold</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.partner} className="border-t border-line hover:bg-page/70">
                      <td className="px-5 py-3 font-medium max-w-[220px] truncate">{r.partner}</td>
                      <td className="px-5 py-3 text-[13px] text-muted">{r.phone ?? r.mobile ?? '—'}</td>
                      <td className="px-5 py-3 text-right tnum text-muted">{lei(r.total_invoiced)}</td>
                      <td className="px-5 py-3 text-right tnum text-muted">{lei(r.total_paid)}</td>
                      <td className="px-5 py-3 text-right">
                        <span className="tnum">{lei(Math.abs(r.advance))}</span>
                        <span className="ml-2">
                          {r.advance > 0 ? <Badge tone="good">avans</Badge> : <Badge tone="warn">datorie</Badge>}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  )
}
